import {isAxiosError} from 'axios';
import type {ReactNode} from 'react';
import {useEffect, useState} from 'react';

import {MarketPlaceTradeStatus as TradeStatus} from '@afriex/core/domain/enums';

import {ClipboardLabel, Link, Modal, SpinningLoader} from '@/components';
import {merchantService} from '@/services/merchants';
import type {MarketPlaceTrade} from '@/types/MarketPlace';

import {
  formatAmount,
  formatDecimal,
  formatMarketPlaceChannel,
  getTradeDate,
  getTradePair,
  getTradeStatusClassName,
  getTradeStatusLabel,
} from './utils/format';

type TradeDetailDrawerProps = {
  tradeId: number | null;
  onClose: () => void;
};

const Row = ({label, children}: {label: string; children: ReactNode}) => (
  <div className="flex items-start justify-between gap-4 py-1 text-sm">
    <span className="shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-right text-gray-800 dark:text-gray-100">
      {children}
    </span>
  </div>
);

const Section = ({title, children}: {title: string; children: ReactNode}) => (
  <section className="border-t border-gray-200 py-3 dark:border-dark-borderMid">
    <h3 className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-100">
      {title}
    </h3>
    {children}
  </section>
);

const getDotClassName = (pending?: boolean, done?: boolean): string => {
  if (pending) {
    return 'border-2 border-yellow-600 bg-white dark:border-yellow-500 dark:bg-dark-inputBg';
  }
  if (done) {
    return 'bg-green-500';
  }
  return 'bg-gray-400 dark:bg-gray-500';
};

const TimelineNode = ({
  title,
  detail,
  done,
  pending,
  className = '',
}: {
  title: string;
  detail: string;
  done?: boolean;
  pending?: boolean;
  className?: string;
}) => (
  <li className="flex gap-3">
    <span
      className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${getDotClassName(
        pending,
        done,
      )}`}
    />
    <div>
      <p
        className={`text-sm font-medium ${className || 'text-gray-800 dark:text-gray-100'}`}
      >
        {title}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>
    </div>
  </li>
);

// The parent remounts this per trade (via `key`), so a new trade starts from
// fresh state and the effect only sets state once the read settles.
const TradeDetailDrawer = ({tradeId, onClose}: TradeDetailDrawerProps) => {
  const [trade, setTrade] = useState<MarketPlaceTrade | null>(null);
  const [isLoading, setIsLoading] = useState(tradeId !== null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (tradeId === null) {
      return undefined;
    }

    const controller = new AbortController();
    merchantService
      .getMarketPlaceTrade(tradeId, controller.signal)
      .then((result) => {
        // Aborted on unmount: nothing is left to update.
        if (controller.signal.aborted) {
          return;
        }
        setTrade(result);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setError(requestError);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [tradeId]);

  const notFound = isAxiosError(error) && error.response?.status === 404;

  return (
    <Modal
      isOpen={tradeId !== null}
      onClose={onClose}
      title={trade ? `Trade #${trade.id}` : `Trade #${tradeId ?? ''}`}
      side="right"
      width="w-full max-w-xl"
      height="h-full"
      closeOnOutsideClick
    >
      {isLoading && (
        <div className="flex justify-center py-10">
          <SpinningLoader />
        </div>
      )}

      {!isLoading && error !== null && (
        <p className="py-6 text-sm text-red-600 dark:text-red-400">
          {notFound
            ? 'This trade is no longer available. It may be a counter-offer awaiting approval, or it may have been removed.'
            : 'Could not load this trade. Close the drawer and try again.'}
        </p>
      )}

      {trade && (
        <div>
          <div className="flex items-center gap-3 pb-2">
            <span
              className={`text-sm font-semibold ${getTradeStatusClassName(trade.status)}`}
            >
              {getTradeStatusLabel(trade.status)}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {getTradePair(trade)}
            </span>
          </div>

          <Section title="Offer">
            <Row label="Offer">#{trade.askId}</Row>
            <Row label="Offer volume">{formatAmount(trade.ask.volume)}</Row>
            <Row label="Offer quote">{formatAmount(trade.ask.quote)}</Row>
            {trade.ask.sellerBusinessId && (
              <Row label="Business offer">{trade.ask.sellerBusinessId}</Row>
            )}
          </Section>

          <Section title="Trade">
            <Row label="Volume">{formatAmount(trade.volume)}</Row>
            <Row label="Price">
              {formatDecimal(trade.price.value)} {trade.price.asset}
            </Row>
            <Row label="Counter amount">
              {formatAmount(trade.counterAmount)}
            </Row>
            <Row label="Funded with">
              {formatMarketPlaceChannel(trade.fundedWith)}
            </Row>
            <Row label="Placed at">{getTradeDate(trade.createdAt)}</Row>
            <Row label="Paid at">{getTradeDate(trade.paidAt)}</Row>
            <Row label="Expires at">{getTradeDate(trade.expiresAt)}</Row>
          </Section>

          <Section title="Counterparty">
            <Row label="Business">
              <Link
                to={`/merchants/businesses/${trade.businessId}`}
                className="text-cyan-700 hover:underline dark:text-cyan-400"
              >
                {trade.businessName || trade.businessId}
              </Link>
            </Row>
            <Row label="Entity ID">{trade.businessId}</Row>
          </Section>

          <Section title="Ledger">
            {trade.ledger ? (
              <div className="space-y-1">
                <Row label="Debit journal">
                  <ClipboardLabel>{trade.ledger.debitJournalId}</ClipboardLabel>
                </Row>
                <Row label="Credit journal">
                  <ClipboardLabel>
                    {trade.ledger.creditJournalId}
                  </ClipboardLabel>
                </Row>
                {trade.ledger.sellerDebitJournalId && (
                  <Row label="Seller debit">
                    <ClipboardLabel>
                      {trade.ledger.sellerDebitJournalId}
                    </ClipboardLabel>
                  </Row>
                )}
                {trade.ledger.sellerCreditJournalId && (
                  <Row label="Seller credit">
                    <ClipboardLabel>
                      {trade.ledger.sellerCreditJournalId}
                    </ClipboardLabel>
                  </Row>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No ledger entries recorded. Paid before journal ids were stored,
                or the bid was left PAID after a failed reversal.
              </p>
            )}
          </Section>

          <Section title="Timeline">
            <ul className="space-y-3">
              <TimelineNode
                title="Bid placed"
                detail={getTradeDate(trade.createdAt)}
                done
              />
              {trade.status === TradeStatus.SETTLING && (
                <TimelineNode
                  title="Awaiting payment"
                  detail={`Expires ${getTradeDate(trade.expiresAt)}`}
                  pending
                  className="text-yellow-700 dark:text-yellow-400"
                />
              )}
              {trade.status === TradeStatus.SETTLED && (
                <TimelineNode
                  title="Settled"
                  detail={getTradeDate(trade.paidAt)}
                  done
                  className="text-green-700 dark:text-green-400"
                />
              )}
              {trade.status === TradeStatus.EXPIRED && (
                <TimelineNode
                  title="Expired"
                  detail={getTradeDate(trade.expiresAt)}
                  className="text-red-600 dark:text-red-400"
                />
              )}
            </ul>
          </Section>
        </div>
      )}
    </Modal>
  );
};

export default TradeDetailDrawer;
